/**
 * XPCV · XBridge (migrada do Streamlit; equivalência em backend/tests_paginas/test_xbridge.py).
 *
 * Cruza o CSV exportado da XBridge com o cadastro de ativos e emissores do Fibery. O
 * arquivo vai uma vez para a API (lido em memória, nada é guardado); as visões e os
 * filtros são aplicados aqui, sobre as linhas devolvidas. Na URL: visão e filtros.
 */
import { Alert, Button, Checkbox, FileInput, Group, SimpleGrid, Stack, Text } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { ErroApi, apiPostForm, type Envelope } from '../../api/cliente';
import { BarraFiltros, CampoOpcoes, GrupoFiltros, LARGURA } from '../../components/Filtros';
import { Kpi, type KpiApi } from '../../components/Kpi';
import { CabecalhoPagina, Pagina, Secao } from '../../components/Layout';
import { TabelaDados, type TabelaApi } from '../../components/TabelaDados';
import { useFiltrosUrl } from '../../lib/url-state';

const CAMINHO = '/carteiras-administradas/xbridge';
const SEM_INDEXADOR = 'Sem indexador';
const VISOES = [
  { valor: 'aprovados', rotulo: 'Aprovados' },
  { valor: 'nao-cadastrados', rotulo: 'Não cadastrados' },
  { valor: 'nao-aprovados', rotulo: 'Cadastrados não aprovados' },
  { valor: 'base', rotulo: 'Base cruzada' },
];

interface Linha extends Record<string, unknown> {
  _aprovado: boolean;
  _cadastrado: boolean;
  _oferta: boolean;
  _isento: boolean;
  _indexador: string | null;
}
interface Dados {
  tabela: Omit<TabelaApi, 'linhas'> & { linhas: Linha[] };
  campos: Record<string, string>;
  colunas_nao_cadastrados: string[];
  colunas_visoes: string[];
  kpis: KpiApi[];
}

function recorte(d: Dados, linhas: Linha[], campos: string[], estilos?: Record<string, string>) {
  return {
    colunas: d.tabela.colunas.filter((c) => campos.includes(c.campo)),
    linhas,
    estilos,
  };
}

/** Contagem por indexador, como o groupby do legado (sem indexador por último). */
function porIndexador(linhas: Linha[]) {
  const contagem = new Map<string, number>();
  for (const l of linhas) {
    const k = l._indexador ?? SEM_INDEXADOR;
    contagem.set(k, (contagem.get(k) ?? 0) + 1);
  }
  return [...contagem.entries()]
    .sort(([a], [b]) =>
      a === SEM_INDEXADOR ? 1 : b === SEM_INDEXADOR ? -1 : a < b ? -1 : a > b ? 1 : 0,
    )
    .sort((a, b) => b[1] - a[1]);
}

function Visao({ d }: { d: Dados }) {
  const [f, definir] = useFiltrosUrl({
    visao: 'aprovados',
    oferta: true as boolean,
    isentos: false as boolean,
    indexador: 'Todos',
  });
  const linhas = d.tabela.linhas;
  const oferta = d.campos['OFFER Mercado'] ?? '';

  const aprovados = useMemo(
    () =>
      linhas.filter((l) => l._aprovado && (!f.oferta || l._oferta) && (!f.isentos || l._isento)),
    [linhas, f.oferta, f.isentos],
  );
  const resumo = useMemo(() => porIndexador(aprovados), [aprovados]);

  if (f.visao === 'nao-cadastrados') {
    const t = recorte(
      d,
      linhas.filter((l) => !l._cadastrado),
      d.colunas_nao_cadastrados,
    );
    return <TabelaDados tabela={t} altura={560} />;
  }
  if (f.visao === 'nao-aprovados') {
    const t = recorte(
      d,
      linhas.filter((l) => l._cadastrado && !l._aprovado),
      d.colunas_visoes,
    );
    return <TabelaDados tabela={t} altura={560} />;
  }
  if (f.visao === 'base') {
    // Linhas não cadastradas destacadas, como no legado.
    const estilos: Record<string, string> = {};
    linhas.forEach((l, i) => {
      if (!l._cadastrado)
        for (const c of d.tabela.colunas) estilos[`${i}:${c.campo}`] = 'background-color: #ffc7ce';
    });
    return <TabelaDados tabela={{ ...d.tabela, estilos }} altura={560} />;
  }

  const rotulos = resumo.map(([k]) => k);
  const indexador = rotulos.includes(f.indexador) ? f.indexador : 'Todos';
  let grade = aprovados;
  if (indexador !== 'Todos') {
    // Um indexador escolhido: ordena por OFFER Mercado decrescente (vazios por último).
    grade = aprovados
      .filter((l) => (l._indexador ?? SEM_INDEXADOR) === indexador)
      .sort((a, b) => {
        const x = a[oferta] as number | null;
        const y = b[oferta] as number | null;
        if (x == null) return y == null ? 0 : 1;
        if (y == null) return -1;
        return y - x;
      });
  }
  return (
    <Stack>
      <Group>
        <Checkbox
          label="Apenas com OFFER disponível"
          checked={f.oferta}
          onChange={(e) => definir({ oferta: e.currentTarget.checked })}
        />
        <Checkbox
          label="Apenas Isentos"
          checked={f.isentos}
          onChange={(e) => definir({ isentos: e.currentTarget.checked })}
        />
      </Group>
      {aprovados.length === 0 ? (
        <Alert color="blue">Nenhum ativo aprovado encontrado com os filtros aplicados.</Alert>
      ) : (
        <>
          <SimpleGrid cols={{ base: 2, md: Math.min(resumo.length, 6) }}>
            {resumo.map(([k, n]) => (
              <Kpi key={k} kpi={{ rotulo: k, valor: String(n) }} />
            ))}
          </SimpleGrid>
          <CampoOpcoes
            rotulo="Indexador"
            valor={indexador}
            aoMudar={(v) => definir({ indexador: v })}
            opcoes={['Todos', ...rotulos].map((r) => ({ valor: r, rotulo: r }))}
          />
          <TabelaDados key={indexador} tabela={recorte(d, grade, d.colunas_visoes)} altura={560} />
        </>
      )}
    </Stack>
  );
}

export function XBridge() {
  const [f, definir] = useFiltrosUrl({ visao: 'aprovados' });
  const [arquivo, setArquivo] = useState<File | null>(null);
  const envio = useMutation({
    mutationFn: () => {
      const form = new FormData();
      form.append('arquivo', arquivo!);
      return apiPostForm<Envelope<Dados>>(CAMINHO, form);
    },
  });
  const d = envio.data?.data;

  return (
    <Pagina>
      <CabecalhoPagina
        titulo="XBridge"
        descricao="Cruza os tickers do CSV exportado da XBridge com o cadastro de ativos (CRI, CRA, Debênture, CDCA) e de emissores do Fibery."
        fonte="XBridge (arquivo enviado) · Fibery (ativos, emissores)"
      />
      <BarraFiltros carregando={envio.isPending}>
        <GrupoFiltros titulo="Arquivo">
          <FileInput
            label="CSV XBridge"
            description="Layout padrão, separado por ponto e vírgula"
            placeholder="Escolher arquivo"
            accept=".csv"
            value={arquivo}
            onChange={setArquivo}
            clearable
            w={LARGURA.selecao + 40}
          />
          <Button mt={25} color="dourado" disabled={!arquivo} onClick={() => envio.mutate()}>
            Processar arquivo
          </Button>
        </GrupoFiltros>
      </BarraFiltros>
      {!d && !envio.isPending && !envio.isError && (
        <Alert color="blue">
          Envie um arquivo CSV para iniciar a análise. O arquivo é lido em memória e não fica
          guardado.
        </Alert>
      )}
      {envio.isError && (
        <Alert color="red">
          {envio.error instanceof ErroApi
            ? envio.error.message
            : 'Não foi possível cruzar os dados da XBridge.'}
        </Alert>
      )}
      {d && (
        <>
          <SimpleGrid cols={{ base: 2, md: 4 }}>
            {d.kpis.map((k) => (
              <Kpi key={k.rotulo} kpi={k} />
            ))}
          </SimpleGrid>
          <Secao titulo="Visão">
            <Stack>
              <CampoOpcoes
                rotulo=""
                valor={f.visao}
                aoMudar={(visao) => definir({ visao })}
                opcoes={VISOES}
              />
              <Visao d={d} />
            </Stack>
          </Secao>
          <Text size="xs" c="dimmed">
            Aprovados: emissor (ou devedor) com status Aprovado ou Name Lending no Fibery.
          </Text>
        </>
      )}
    </Pagina>
  );
}
